// VisionCreateDialog.js
import React, { useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { createVision } from "../../api/visions";
import PlanIntelligenceSuggestion from "./PlanIntelligenceSuggestion";
import PendingActionNotice from "../common/PendingActionNotice";

function VisionCreateDialog({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [titleError, setTitleError] = useState("");

  async function handleSave() {
    setLoading(true);
    setErrorMessage("");
    setTitleError("");

    try {
      const created = await createVision({ title: title.trim(), description });
      onSaved?.(created);
      onClose();
    } catch (error) {
      setErrorMessage(error.message || "Unable to create vision.");
      const fieldError = error?.errors?.title;
      if (Array.isArray(fieldError) && fieldError.length) {
        setTitleError(fieldError[0]);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Your Vision</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {loading ? (
            <PendingActionNotice
              compact
              title="Creating vision"
              body="Saving your direction and preparing the next goal step."
            />
          ) : null}

          <Typography variant="body2" color="text.secondary">
            Your vision is the long-term direction you care about most. It changes
            rarely. Next you will add a measurable goal that makes it real.
          </Typography>
          <TextField
            label="Vision title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            fullWidth
            required
            error={Boolean(titleError)}
            helperText={titleError}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <PlanIntelligenceSuggestion
            itemType="vision"
            payload={{ title, description }}
            applyLabel="Use this wording"
            onApply={(suggestion) => {
              if (suggestion.suggested_title) setTitle(suggestion.suggested_title);
              if (suggestion.suggested_description) setDescription(suggestion.suggested_description);
            }}
          />
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || loading}
        >
          {loading ? "Creating..." : "Create Vision"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VisionCreateDialog;
